import React from 'react';
import { Link } from 'react-router-dom';
import './HomeV2.css';
import useScrollReveal from '../hooks/useScrollReveal';
import HeroReportPreview from './HeroReportPreview';
import { StepEnvelope, StepIdVerify, StepReport, StepConnector } from './StepFlowGraphic';
import PageSEO from './PageSEO';

function HomeV2() {
  const revealRef = useScrollReveal();

  return (
    <main className="App-main v2-home" ref={revealRef}>
      <PageSEO title="Tenant Screening for Canadian Landlords | $29 Credit Check & AI Assessment | Rentual" description="Screen tenants in minutes with TransUnion credit checks, identity verification, and AI risk assessment. $29 per report. Built for Ontario landlords." path="/" />

      {/* ── Hero ──────────────────────────── */}
      <section className="v2-hero">
        <div className="v2-hero-inner">
          <h1 className="v2-reveal">
            Tenant screening<br />
            <span className="v2-highlight">in minutes. $29/report.</span>
          </h1>
          <p className="v2-subhead v2-reveal" data-reveal-delay="100">
            Credit check, income verification, and risk assessment, all in one report. You pay or pass the cost to your tenant.
          </p>
          <div className="v2-ctas v2-reveal" data-reveal-delay="200">
            <Link to="/register?intent=screen" className="v2-cta-primary">
              Screen a tenant →
            </Link>
            <Link to="/sample-report" className="v2-cta-secondary">
              See a sample report
            </Link>
          </div>
          <p className="v2-trust-line v2-reveal" data-reveal-delay="300">Landlord or tenant pays · No commitment · Results in minutes</p>

          <HeroReportPreview />
        </div>
      </section>

      {/* ── Trust Bar ─────────────────────── */}
      <section className="v2-trust-bar v2-reveal">
        <div className="v2-trust-items">
          <div className="v2-trust-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            <span>Real credit data</span>
          </div>
          <div className="v2-trust-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>Built for Canadian landlords</span>
          </div>
          <div className="v2-trust-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>Reports in minutes</span>
          </div>
        </div>
      </section>

      {/* ── Who Pays ─────────────────────── */}
      <section className="v2-how" id="who-pays">
        <div className="v2-section-head v2-reveal">
          <h2>You choose who pays</h2>
          <p>Screen tenants at no cost to you, or pay upfront for faster results.</p>
        </div>
        <div className="v2-pays-grid">
          <div className="v2-pays-card v2-reveal" data-reveal-delay="0">
            <div className="v2-pays-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
            </div>
            <h3>Landlord pays</h3>
            <p>Pay $29, send your tenant a link. They fill out a form and you get the report. Fast and simple.</p>
          </div>
          <div className="v2-pays-card v2-pays-card--popular v2-reveal" data-reveal-delay="100">
            <div className="v2-pays-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v-2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
            </div>
            <h3>Tenant pays</h3>
            <p>Send a free screening invite. Your tenant pays the $29 fee when they submit. Zero cost to you.</p>
            <span className="v2-pays-badge">Most popular</span>
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────── */}
      <section className="v2-how" id="how-it-works">
        <div className="v2-section-head v2-reveal">
          <h2>How it works</h2>
          <p>Three steps. No property listing required.</p>
        </div>
        <div className="v2-steps-flow">
          <div className="v2-step-card v2-reveal" data-reveal-delay="0">
            <StepEnvelope />
            <div className="v2-step-num">1</div>
            <h3>Enter your tenant's details</h3>
            <p>Their email and your rental address. That's it. No property listing, no setup.</p>
          </div>
          <StepConnector />
          <div className="v2-step-card v2-reveal" data-reveal-delay="120">
            <StepIdVerify />
            <div className="v2-step-num">2</div>
            <h3>They verify identity and complete a short form</h3>
            <p>Your tenant verifies their ID, fills in employment and income info, then consents to a soft credit check.</p>
          </div>
          <StepConnector />
          <div className="v2-step-card v2-reveal" data-reveal-delay="240">
            <StepReport />
            <div className="v2-step-num">3</div>
            <h3>Get your report</h3>
            <p>Credit score, income details, risk level, and a plain-English summary. All in one page.</p>
          </div>
        </div>
      </section>

      {/* ── What's In The Report ──────────── */}
      <section className="v2-report">
        <div className="v2-section-head v2-reveal">
          <h2>What's in the report</h2>
          <p>Everything you need to make a confident decision.</p>
        </div>
        <div className="v2-report-grid">
          <div className="v2-report-card v2-reveal" data-reveal-delay="0">
            <div className="v2-report-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </div>
            <h4>Credit report</h4>
            <p>Credit score, outstanding debts, collections, bankruptcies, and account history.</p>
          </div>
          <div className="v2-report-card v2-reveal" data-reveal-delay="100">
            <div className="v2-report-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
            </div>
            <h4>Income check</h4>
            <p>Self-reported income with rent-to-income ratio. Upgrade to Pro for verified income via document uploads.</p>
          </div>
          <div className="v2-report-card v2-reveal" data-reveal-delay="200">
            <div className="v2-report-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            </div>
            <h4>Risk assessment</h4>
            <p>Risk level, red flags, and a plain-English summary. No jargon, just a clear recommendation.</p>
          </div>
          <div className="v2-report-card v2-reveal" data-reveal-delay="300">
            <div className="v2-report-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </div>
            <h4>Full dossier</h4>
            <p>Everything in one page. Compare applicants side by side when screening for the same unit.</p>
          </div>
        </div>
      </section>

      {/* ── Pricing ──────────────────────── */}
      <section className="v2-pricing" id="pricing">
        <div className="v2-section-head v2-reveal">
          <h2>Simple pricing</h2>
          <p>Pay per report. Upgrade when you need more.</p>
        </div>
        <div className="v2-pricing-grid">
          <div className="v2-price-card v2-reveal" data-reveal-delay="0">
            <h3>Screening</h3>
            <div className="v2-price-amount">
              <span className="v2-dollar">$29</span>
              <span className="v2-period">/report</span>
            </div>
            <p className="v2-price-desc">Quick credit check for any tenant.</p>
            <div className="v2-price-cta">
              <Link to="/register?intent=screen" className="v2-cta-primary">Screen a tenant</Link>
            </div>
            <ul>
              <li>Credit check</li>
              <li>Risk assessment</li>
              <li>Identity verification</li>
              <li>Self-reported income</li>
              <li>Landlord or tenant pays</li>
              <li>No subscription required</li>
            </ul>
          </div>

          <div className="v2-price-card v2-popular v2-reveal v2-reveal--scale" data-reveal-delay="120">
            <span className="v2-price-badge">Best value</span>
            <h3>Pro</h3>
            <div className="v2-price-amount">
              <span className="v2-dollar">$20</span>
              <span className="v2-period">/month</span>
            </div>
            <p className="v2-price-desc">Full platform. $25/report.</p>
            <div className="v2-price-cta">
              <Link to="/platform" className="v2-cta-primary">Start Pro</Link>
            </div>
            <ul>
              <li>Screening at $25/report</li>
              <li>Verified income (doc uploads)</li>
              <li>Document fraud detection</li>
              <li>Ontario lease generation</li>
              <li>E-signatures</li>
              <li>Rent collection + autopay</li>
              <li>Unlimited properties</li>
              <li>Payment tracking + receipts</li>
            </ul>
          </div>

          <div className="v2-price-card v2-reveal" data-reveal-delay="240">
            <h3>Pro Annual</h3>
            <div className="v2-price-amount">
              <span className="v2-dollar">$200</span>
              <span className="v2-period">/year</span>
            </div>
            <p className="v2-price-desc">Save $40. Everything in Pro.</p>
            <div className="v2-price-cta">
              <Link to="/register?intent=pro" className="v2-cta-secondary">Start annual</Link>
            </div>
            <ul>
              <li>Everything in Pro</li>
              <li>Save $40/year</li>
              <li>Priority support</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── Platform Upsell ──────────────── */}
      <section className="v2-platform">
        <div className="v2-section-head v2-reveal">
          <h2>Need more than screening?</h2>
          <p>Once you've found the right tenant, Rentual helps you manage everything.</p>
        </div>
        <div className="v2-platform-grid">
          <div className="v2-platform-card v2-reveal" data-reveal-delay="0">
            <h4>📋 Generate Ontario leases</h4>
            <p>Auto-filled from the screening. Standard lease form, e-signatures in minutes.</p>
          </div>
          <div className="v2-platform-card v2-reveal" data-reveal-delay="100">
            <h4>💳 Collect rent automatically</h4>
            <p>Card or pre-authorized debit. Autopay, reminders, and receipts for both parties.</p>
          </div>
          <div className="v2-platform-card v2-reveal" data-reveal-delay="200">
            <h4>🏠 List your properties</h4>
            <p>Shareable listing link. Tenants apply online. Track everything in one dashboard.</p>
          </div>
        </div>
        <div className="v2-reveal" data-reveal-delay="300" style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Link to="/platform" className="v2-cta-secondary">See all features</Link>
        </div>
      </section>

      {/* ── Final CTA ────────────────────── */}
      <section className="v2-final-cta v2-reveal">
        <h2>Screen your first tenant today.</h2>
        <p>$29 per report. You pay or your tenant pays. No subscription. Results in minutes.</p>
        <div className="v2-ctas">
          <Link to="/register?intent=screen" className="v2-cta-primary">
            Screen a tenant →
          </Link>
        </div>
      </section>

    </main>
  );
}

export default HomeV2;
